import { useEffect, useState } from "react";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { rakebackBalance } from "@/hooks/firestore/models/rakebackBalance";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { message } from "@/utilities/message/message";
import { convertErrorToToast } from "@/utilities/errorsParser";
import { RakebackChips, toast } from "@picklebet/ui";

const useRakebackBalance = () => {
  const currency = useFiatCurrency();

  const {
    data: rakeback,
    isLoading,
    error,
  } = useFirestore("/users/{userId}/rakebackBalance", rakebackBalance.array());

  const [isClaiming, setIsClaiming] = useState(false);
  const [hasFreshData, setHasFreshData] = useState(false);

  useEffect(() => {
    if (!isLoading) setHasFreshData(true);
  }, [isLoading]);

  // firestore has middleware showing stale data while subscriptions catchup,
  // so this forces the first render to always show as a loading state.
  const effectiveIsLoading = !hasFreshData || isLoading;

  const handleClaim = async () => {
    setIsClaiming(true);
    const [response, claimError] = await message("claim-rakeback-balance");
    setIsClaiming(false);

    if (claimError) {
      toast(
        convertErrorToToast(claimError, {
          description:
            "Unable to claim rakeback, try again or contact support.",
        }),
      );
      return;
    }

    toast({
      variant: "info",
      icon: RakebackChips,
      description: `${response?.displayText || "Rakeback claimed successfully"}.`,
    });
  };

  return {
    rakeback: rakeback?.find((r) => r.currency === currency),
    isLoading: effectiveIsLoading,
    handleClaim,
    isClaiming,
    error,
  };
};

type RakebackBalance = ReturnType<typeof useRakebackBalance>;

export { useRakebackBalance };
export type { RakebackBalance };
